import React, { useEffect, useState, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/components/StatusBadge";
import { Building2, Flag, Landmark, Wallet, ChevronRight, ChevronDown } from "lucide-react";
import { MonthlyInrCard } from "@/components/MonthlyInrCard";

const fmt = (n, cur = "USD") =>
  formatCurrency(n || 0, cur);

// Abbreviate large numbers (1_250_000 -> "1.25M") while keeping the full-precision value
// available for a tooltip via the `title` attribute.
function abbrev(n) {
  const val = Number(n || 0);
  const abs = Math.abs(val);
  if (abs >= 1e9) return `${(val / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
  if (abs >= 1e5) return `${(val / 1e3).toFixed(1)}K`;
  return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
const DONUT_COLORS = ["#FF7F00", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#EF4444"];

function ServiceTypeDonut({ rows, onSliceClick, selected }) {
  const chartData = rows.slice(0, 8).map((r) => ({ name: r.service_type, value: r.total_amount_inr }));
  const total = chartData.reduce((s, r) => s + r.value, 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 items-center" data-testid="revenue-service-type-donut">
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={chartData}
              innerRadius={55} outerRadius={90} paddingAngle={2}
              dataKey="value" stroke="none"
              onClick={(e) => onSliceClick?.(e?.name)}
              className="cursor-pointer"
            >
              {chartData.map((r, i) => (
                <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]}
                      fillOpacity={selected && selected !== r.name ? 0.35 : 1} />
              ))}
            </Pie>
            <RTooltip formatter={(v) => new Intl.NumberFormat("en-IN").format(Math.round(v))} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1.5">
        {chartData.map((r, i) => (
          <button
            type="button"
            key={r.name}
            onClick={() => onSliceClick?.(r.name)}
            data-testid={`donut-legend-${r.name}`}
            className={`w-full flex items-center gap-2 text-xs text-left rounded px-1 py-0.5 hover:bg-slate-50 ${selected === r.name ? "bg-brand/10" : ""}`}
          >
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="flex-1 text-slate-700">{r.name}</span>
            <span className="tabular-nums text-slate-800 font-medium">
              <AbbrevAmount value={r.value} currency="INR" />
            </span>
            <span className="text-slate-400 tabular-nums w-10 text-right">
              {total ? Math.round((r.value / total) * 100) : 0}%
            </span>
          </button>
        ))}
        {selected && (
          <button type="button" onClick={() => onSliceClick?.(null)}
                  className="text-xs text-slate-500 underline hover:text-slate-700 mt-2"
                  data-testid="donut-clear-filter">
            Clear filter
          </button>
        )}
      </div>
    </div>
  );
}

function AbbrevAmount({ value, currency }) {
  return (
    <span title={fmt(value, currency)} className="cursor-help">
      {currency} {abbrev(value)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Currency-aware totals block (a horizontal group of small tiles)    */
/* ------------------------------------------------------------------ */
function CurrencyTiles({ label, buckets, tone = "slate", icon: Icon }) {
  const toneClasses = {
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    sky: "bg-sky-50 border-sky-200 text-sky-800",
  };
  return (
    <Card className="border-slate-200 shadow-none p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
        {Icon ? <Icon className="w-4 h-4" /> : null} {label}
      </div>
      {(!buckets || buckets.length === 0) ? (
        <div className="text-slate-400 mt-3 text-sm">No data</div>
      ) : (
        <div className="mt-3 space-y-3">
          {buckets.map((b) => (
            <div key={b.currency} className={`rounded-lg border px-3 py-2 ${toneClasses[tone]}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase font-medium opacity-70">{b.currency}</span>
                <span className="text-[11px] opacity-60">{b.projects} project{b.projects === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 mt-1">
                <MetricLine label="Project value" v={b.project_value} cur={b.currency} />
                <MetricLine label="Invoiced" v={b.invoiced} cur={b.currency} />
                <MetricLine label="Collected" v={b.collected} cur={b.currency} strong="collected" />
                <MetricLine label="Outstanding" v={b.outstanding} cur={b.currency} strong="outstanding" />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function MetricLine({ label, v, cur, strong }) {
  const strongClass =
    strong === "collected" ? "text-emerald-700"
    : strong === "outstanding" ? "text-rose-700"
    : "";
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase opacity-60 truncate">{label}</div>
      <div className={`font-semibold tabular-nums truncate ${strongClass}`} title={fmt(v, cur)}>
        {abbrev(v)}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Collapsible aggregation table (BD, Client)                         */
/* ------------------------------------------------------------------ */
function GroupTable({ title, rows }) {
  const [expanded, setExpanded] = useState({});
  return (
    <Card className="border-slate-200 shadow-none p-6 overflow-hidden">
      <div className="font-heading font-medium mb-3">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-slate-500 text-sm py-3">No data in this window.</div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="text-left">
                <th className="px-3 py-2 w-6"></th>
                <th className="px-3 py-2 sticky left-0 bg-slate-50 z-20 min-w-[180px]">Name</th>
                <th className="px-3 py-2 text-right">Projects</th>
                <th className="px-3 py-2 text-right">Project Value</th>
                <th className="px-3 py-2 text-right">Invoiced</th>
                <th className="px-3 py-2 text-right">Collected</th>
                <th className="px-3 py-2 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const totalProjects = r.by_currency.reduce((s, b) => s + b.projects, 0);
                const primary = r.by_currency[0] || { project_value: 0, invoiced: 0, collected: 0, outstanding: 0, currency: "USD" };
                const showExpand = r.by_currency.length > 1;
                const open = !!expanded[r.name];
                return (
                  <React.Fragment key={r.name}>
                    <tr
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => showExpand && setExpanded((e) => ({ ...e, [r.name]: !e[r.name] }))}
                    >
                      <td className="px-3 py-2 text-slate-400">
                        {showExpand ? (open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : null}
                      </td>
                      <td className="px-3 py-2 truncate max-w-[220px] sticky left-0 bg-white z-10" title={r.name}>{r.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{totalProjects}</td>
                      {r.by_currency.length === 1 ? (
                        <>
                          <td className="px-3 py-2 text-right tabular-nums"><AbbrevAmount value={primary.project_value} currency={primary.currency} /></td>
                          <td className="px-3 py-2 text-right tabular-nums"><AbbrevAmount value={primary.invoiced} currency={primary.currency} /></td>
                          <td className="px-3 py-2 text-right tabular-nums text-emerald-700"><AbbrevAmount value={primary.collected} currency={primary.currency} /></td>
                          <td className="px-3 py-2 text-right tabular-nums text-rose-700"><AbbrevAmount value={primary.outstanding} currency={primary.currency} /></td>
                        </>
                      ) : (
                        <td colSpan={4} className="px-3 py-2 text-right text-xs text-slate-500">
                          Multi-currency ({r.by_currency.map((b) => b.currency).join(", ")}) — click to expand
                        </td>
                      )}
                    </tr>
                    {open && r.by_currency.map((b) => (
                      <tr key={b.currency} className="bg-slate-50/60 border-t border-slate-100">
                        <td></td>
                        <td className="px-3 py-1.5 text-xs text-slate-500 sticky left-0 bg-slate-50/60 z-10">↳ {b.currency}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{b.projects}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums"><AbbrevAmount value={b.project_value} currency={b.currency} /></td>
                        <td className="px-3 py-1.5 text-right tabular-nums"><AbbrevAmount value={b.invoiced} currency={b.currency} /></td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700"><AbbrevAmount value={b.collected} currency={b.currency} /></td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-rose-700"><AbbrevAmount value={b.outstanding} currency={b.currency} /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Projects detail table                                              */
/* ------------------------------------------------------------------ */
function ProjectRows({ rows }) {
  return (
    <Card className="border-slate-200 shadow-none p-6">
      <div className="flex justify-between items-center mb-3">
        <div className="font-heading font-medium">Projects in this window ({rows.length})</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-slate-500 text-sm py-3">No projects fall in this financial year.</div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="max-h-[520px] overflow-auto">
            <table className="w-full text-sm min-w-[960px]">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left">
                  <th className="px-3 py-2 sticky left-0 bg-slate-50 z-20 min-w-[160px]">Client</th>
                  <th className="px-3 py-2 min-w-[180px]">Project</th>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">BD</th>
                  <th className="px-3 py-2 text-center">Entity</th>
                  <th className="px-3 py-2 text-right">Project Value</th>
                  <th className="px-3 py-2 text-right">Invoiced</th>
                  <th className="px-3 py-2 text-right">Collected</th>
                  <th className="px-3 py-2 text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody data-testid="revenue-projects-table">
                {rows.map((r) => (
                  <tr key={r.project_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-1.5 truncate max-w-[200px] sticky left-0 bg-white z-10" title={r.client_name}>{r.client_name}</td>
                    <td className="px-3 py-1.5 truncate max-w-[220px]" title={r.project_name}>{r.project_name}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{r.project_code || "—"}</td>
                    <td className="px-3 py-1.5 text-slate-600">{r.bd_name}</td>
                    <td className="px-3 py-1.5 text-center">
                      <span className={`inline-flex text-[10px] rounded-full px-2 py-0.5 border ${r.entity === "india" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-800 border-slate-300"}`}>
                        {r.entity === "india" ? "India" : "LLC"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums"><AbbrevAmount value={r.project_value} currency={r.currency} /></td>
                    <td className="px-3 py-1.5 text-right tabular-nums"><AbbrevAmount value={r.invoiced} currency={r.currency} /></td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700"><AbbrevAmount value={r.collected} currency={r.currency} /></td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-rose-700"><AbbrevAmount value={r.outstanding} currency={r.currency} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Main Revenue page                                                  */
/* ------------------------------------------------------------------ */
export default function Revenue() {
  const [fy, setFy] = useState(""); // "" = current
  const [entity, setEntity] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthlyInr, setMonthlyInr] = useState(null);   // POV: monthly INR revenue card
  const [byServiceType, setByServiceType] = useState(null); // admin/accounts only
  const [selectedServiceType, setSelectedServiceType] = useState(null); // donut drill-through

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/revenue/fy?fy=${fy}&entity=${entity}`);
      setData(data);
      const fyParam = fy || (data?.available_fys?.[0] || "");
      api.get(`/api/dashboard/monthly-inr-received${fyParam ? `?financial_year=${fyParam}` : ""}`)
        .then((r) => setMonthlyInr(r.data))
        .catch(() => setMonthlyInr(null));
      // Admin/Accounts get an additional per-service-type breakdown.
      // BDs receive 403 here — swallow silently.
      api.get(`/api/revenue/by-service-type?fy=${fyParam}${entity !== "all" ? `&entity=${entity}` : ""}`)
        .then((r) => setByServiceType(r.data))
        .catch(() => setByServiceType(null));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy, entity]);

  const availableFYs = useMemo(() => {
    const list = data?.available_fys || [];
    return list.length ? list : ["25-26"];
  }, [data]);

  if (!data && loading) return <div className="text-slate-500">Loading…</div>;
  const totals = data?.totals || { grand: [], by_entity: { llc: [], india: [] } };

  return (
    <div className="space-y-6" data-testid="revenue-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight">Revenue</h1>
          <p className="text-sm text-slate-500 mt-1">Financial-year rollup with LLC vs India split. FY runs April 1 → March 31.</p>
        </div>
        <div className="flex gap-3 items-end">
          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Financial year</label>
            <Select value={fy || (availableFYs[0] || "25-26")} onValueChange={(v) => setFy(v)}>
              <SelectTrigger className="w-40" data-testid="revenue-fy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableFYs.map((f) => (
                  <SelectItem key={f} value={f} data-testid={`fy-option-${f}`}>FY {f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Entity</label>
            <div className="flex gap-1 border border-slate-200 rounded-lg p-1 bg-white">
              {[
                { v: "all", label: "All" },
                { v: "llc", label: "LLC" },
                { v: "india", label: "India" },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setEntity(o.v)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition ${entity === o.v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`entity-toggle-${o.v}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Grand totals split by entity */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CurrencyTiles label={`LLC · FY ${data?.fy || ""}`} buckets={totals.by_entity.llc} tone="slate" icon={Building2} />
        <CurrencyTiles label={`India · FY ${data?.fy || ""}`} buckets={totals.by_entity.india} tone="amber" icon={Flag} />
        <CurrencyTiles label={`Combined · FY ${data?.fy || ""}`} buckets={totals.grand} tone="emerald" icon={Landmark} />
      </div>

      {/* Monthly INR received — chart + table (admin/accounts only route) */}
      {monthlyInr && <MonthlyInrCard data={monthlyInr} />}

      {/* Revenue by service type — admin/accounts only (endpoint 403s BD) */}
      {byServiceType && byServiceType.rows && byServiceType.rows.length > 0 && (() => {
        const rows = selectedServiceType
          ? byServiceType.rows.filter((r) => r.service_type === selectedServiceType)
          : byServiceType.rows;
        return (
        <Card className="border-slate-200 shadow-none p-6" data-testid="revenue-by-service-type-card">
          <div className="font-heading font-medium mb-1">Revenue by Service Type</div>
          <div className="text-xs text-slate-500 mb-3">
            Which service lines are generating the most revenue this FY. Click a slice / legend row
            to filter the table below. Amounts translated to INR using each invoice's stamped FX rate.
          </div>
          <ServiceTypeDonut
            rows={byServiceType.rows}
            selected={selectedServiceType}
            onSliceClick={(name) => setSelectedServiceType((prev) => prev === name ? null : name)}
          />
          <div className="border border-slate-200 rounded-lg overflow-hidden mt-4">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-3 py-2">Service / Project Type</th>
                  <th className="px-3 py-2 text-right">Invoices</th>
                  <th className="px-3 py-2 text-right">Total (INR translated)</th>
                </tr>
              </thead>
              <tbody data-testid="revenue-by-service-type-tbody">
                {rows.map((r) => (
                  <tr key={r.service_type} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-medium">{r.service_type}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{r.invoice_count}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      <AbbrevAmount value={r.total_amount_inr} currency="INR" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        );
      })()}

      {/* By BD + By Client */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GroupTable title="By Business Developer" rows={data?.by_bd || []} />
        <GroupTable title="By Client" rows={data?.by_client || []} />
      </div>

      {/* Projects detail */}
      <ProjectRows rows={data?.project_rows || []} />
    </div>
  );
}
